import React, { useContext } from 'react';

import { Row, Col } from 'antd';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import { getMoment } from '@libs/NwMoment';
import { getTime, isAllDay } from '@libs/NwMoment';
import { validatePeriod } from '@libs/NwUtils';

import { ListContext } from '@contexts/ListContext';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import {
    useUpdateFollowup,
    useUpdateGroupedCommonFollowUp,
    useUpdatePeriodGroupedCommonFollowUp,
} from '@hooks/API/Event/UseFollowup';
import useNwBreakPoints from '@hooks/Layout/UseNwBreakPoints';

import { NwCancelButton, NwSaveButton } from '@components/Gui/NwButton';
import {
    NWContactDropdown,
    NWFollowUpLabel,
    NWTextAreaCharCounter,
    NWInput,
    NWPeriodSelector,
    NWSwitch,
    NWLongEventPeriodPicker,
} from '@components/Gui/NwForm/NwFormItems';
import { NwForm, NwFormButtonsBar } from '@components/Gui/NwForm/NwFormWrapper';

import { useFollowUpState } from './FollowUpContext';

const FollowUpFormEdit = ({ onTouchForm, onUpdateFollowupData, viewMode }) => {
    const { afterUpdate, groupType, modelId, followUpData, onCancel } = useFollowUpState();
    const breakpoints = useNwBreakPoints();
    const { cachedList } = useContext(ListContext);
    const followUpLabels = cachedList.followUpLabels.items || [];
    const { mutateAsync: onUpdateFollowup } = useUpdateFollowup();
    const { mutateAsync: onUpdateGroupedCommonFollowUp } = useUpdateGroupedCommonFollowUp();
    const { mutateAsync: onUpdatePeriodGroupedCommonFollowUp } = useUpdatePeriodGroupedCommonFollowUp();

    const initialValues = {
        ServiceID: followUpData.ServiceID,
        FollowUpLabelCode: followUpData.FollowUpLabelCode,
        Title: followUpData.Title,
        ModelNotAvailable: followUpData.ModelNotAvailable,
        ModelID: followUpData.ModelID,
    };
    if (!groupType) {
        initialValues.Description = followUpData.Description;
        initialValues.ID = followUpData.ID;
    }
    if (groupType !== 'period') {
        initialValues.StartDate = getMoment(followUpData.StartDate);
        initialValues.EndDate = getMoment(followUpData.EndDate);
        initialValues.Period = {
            startDate: getMoment(followUpData.StartDate),
            endDate: getMoment(followUpData.EndDate),
            startTime: getTime(getMoment(followUpData.StartDate)),
            endTime: getTime(getMoment(followUpData.EndDate)),
            allDay: isAllDay(getMoment(followUpData.StartDate), getMoment(followUpData.EndDate)),
            dates: [],
        };
        initialValues.ModelID = followUpData.ModelID;
    }
    if (groupType === 'models') {
        initialValues.GroupGuid = followUpData.GroupGuid;
    }
    if (groupType === 'period') {
        initialValues.PeriodGroupGuid = followUpData.PeriodGroupGuid;
    }

    const validationSchema = Yup.object().shape({
        Title: Yup.string().required('Title is required'),
        ModelID: groupType === 'models' ? Yup.string().notRequired() : Yup.string().required('Model is required'),
        FollowUpLabelCode: Yup.string().required('Follow up label is required'),
    });

    const submitForm = async (values, setStatus, setSubmitting) => {
        // remove Period from form values. I will use updatedFollowUpData for the update.
        const { Period: _period, ...updatedFollowUpData } = {
            ...initialValues,
            ...values,
        };

        //add dates only if groupType is not period
        if (groupType !== 'period') {
            const sd = getMoment(values.Period.startDate)
                .hour(values.Period.startTime[0])
                .minute(values.Period.startTime[1]);
            const ed = getMoment(values.Period.endDate).hour(values.Period.endTime[0]).minute(values.Period.endTime[1]);
            updatedFollowUpData.StartDate = sd;
            updatedFollowUpData.EndDate = ed;
        }

        try {
            if (groupType) {
                if (groupType === 'models') {
                    await onUpdateGroupedCommonFollowUp({ followupData: updatedFollowUpData });
                } else {
                    await onUpdatePeriodGroupedCommonFollowUp({ followupData: updatedFollowUpData });
                }
            } else {
                await onUpdateFollowup({ followupData: updatedFollowUpData });
            }
            if (afterUpdate) {
                afterUpdate();
            }
            onTouchForm(false);
            onUpdateFollowupData();
            if (!viewMode || viewMode === 'create') {
                handleCancel();
            }
        } catch (error) {
            console.error('Update Followup Error => ', error);
        }
        setSubmitting(false);
    };

    const beforeSubmit = async (values, setStatus, setSubmitting) => {
        await submitForm(values, setStatus, setSubmitting);
        if (onTouchForm) {
            onTouchForm(false);
        }
    };

    const handleCancel = () => {
        if (onTouchForm) {
            onTouchForm(false);
        }
        onCancel();
    };

    const handleFollowUpLabelChange = (values, setFieldValue, value) => {
        const followup = followUpLabels.find((item) => item.Code === value);
        if (!values.Title) {
            setFieldValue(
                'Title',
                `${followup ? followup.Label : ''}${values.ServiceID ? ` - ${getContactName(values.ServiceID)}` : ''}`
            );
        }
        setFieldValue('ModelNotAvailable', followup && followup.ModelNotAvailable);
    };

    const handleServiceChange = (values, setFieldValue, value) => {
        if (!values.Title) {
            const followup = followUpLabels.find((item) => item.Code === values.FollowUpLabelCode);
            setFieldValue('Title', `${followup.Label} - ${getContactName(value)}`);
        }
    };

    const getColSpan = (defaultSpan = 12) => {
        return breakpoints === 1 ? 24 : defaultSpan;
    };

    return (
        <div className="event-form">
            <Formik
                initialValues={initialValues}
                enableReinitialize={false}
                validationSchema={validationSchema}
                onSubmit={(values, { setStatus, setSubmitting }) => {
                    setStatus();
                    beforeSubmit(values, setStatus, setSubmitting);
                }}
            >
                {({ isSubmitting, values, setFieldValue, handleSubmit }) => (
                    <NwForm small values={values} onTouchForm={onTouchForm} onFinish={handleSubmit} layout="vertical">
                        <Row gutter={10}>
                            {!modelId && groupType !== 'models' && (
                                <Col span={24}>
                                    <Field
                                        component={NWContactDropdown}
                                        label="Model"
                                        name="ModelID"
                                        value={values.ModelID}
                                        activeTypes={['mod']}
                                        activableTypes={[]}
                                        required
                                    />
                                </Col>
                            )}
                            <Col span={getColSpan()}>
                                <Field
                                    component={NWFollowUpLabel}
                                    label="Activity Type"
                                    name="FollowUpLabelCode"
                                    value={values.FollowUpLabelCode}
                                    onAfterChange={(value) => handleFollowUpLabelChange(values, setFieldValue, value)}
                                />
                            </Col>
                            <Col span={getColSpan()}>
                                <Field
                                    component={NWContactDropdown}
                                    label="Service"
                                    name="ServiceID"
                                    value={values.ServiceID}
                                    activeTypes={['srv']}
                                    activableTypes={[]}
                                    onAfterChange={(value) => handleServiceChange(values, setFieldValue, value)}
                                />
                            </Col>
                            {groupType !== 'period' && (
                                <Col span={24}>
                                    <Field
                                        component={NWLongEventPeriodPicker}
                                        label="Period"
                                        name="Period"
                                        value={values.Period}
                                    />
                                </Col>
                            )}
                            <Col span={24}>
                                <Field
                                    component={NWInput}
                                    label="Title"
                                    name="Title"
                                    type="text"
                                    value={values.Title}
                                />
                            </Col>
                            {!groupType && (
                                <Col span={24}>
                                    <Field
                                        component={NWTextAreaCharCounter}
                                        label="Description"
                                        name="Description"
                                        type="text"
                                        value={values.Description}
                                        maxLength={2000}
                                    />
                                </Col>
                            )}
                            <Col span={24}>
                                <div style={{ margin: '2rem 0' }}>
                                    <Field
                                        component={NWSwitch}
                                        text="set model as not available"
                                        name="ModelNotAvailable"
                                        value={values.ModelNotAvailable}
                                        required
                                    />
                                </div>
                            </Col>
                        </Row>
                        <NwFormButtonsBar
                            left={<NwCancelButton disabled={isSubmitting} onClick={handleCancel} />}
                            right={<NwSaveButton htmlType="submit" disabled={isSubmitting} loading={isSubmitting} />}
                        />
                    </NwForm>
                )}
            </Formik>
        </div>
    );
};

export default React.memo(FollowUpFormEdit);
